import React from 'react';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

interface AboutProps {
  onNavigate: (page: string) => void;
}

const About: React.FC<AboutProps> = ({ onNavigate }) => {
  useSEO({
    title: 'About UTXOMacro',
    description: 'UTXOMacro is an independent Bitcoin and macroeconomics intelligence platform delivering institutional-grade research for individual investors.',
    url: 'https://utxomacro.com/about',
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A0A0A' }}>
      <main className="flex-1">
        {/* Hero */}
        <div className="relative overflow-hidden border-b border-white/5">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(247,147,26,0.08) 0%, transparent 70%)',
            }}
          />
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
            <div className="text-[#F7931A] text-xs font-bold uppercase tracking-[0.2em] mb-4">
              About
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Independent Intelligence.<br />
              <span className="text-[#F7931A]">Zero Noise.</span>
            </h1>
            <div className="w-12 h-0.5 bg-[#F7931A] rounded mb-6" />
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl">
              UTXOMacro delivers daily Bitcoin on-chain analysis, macro intelligence, and AI disruption signals — the kind of research that used to cost thousands per year, now free.
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-14">
          {/* Mission */}
          <section>
            <h2 className="text-lg font-bold text-[#F7931A] uppercase tracking-wider mb-4">
              The Mission
            </h2>
            <div className="text-gray-400 leading-relaxed space-y-4 text-sm">
              <p>
                Institutional investors have always had an edge — dedicated research desks, Bloomberg terminals, access to on-chain data services that cost $10k/year. Individual investors get CNBC and Twitter.
              </p>
              <p>
                UTXOMacro exists to close that gap. We analyze the same data, build the same models, and publish it daily — free.
              </p>
              <p>
                The name comes from the Bitcoin protocol: a UTXO (Unspent Transaction Output) is the most atomic unit of Bitcoin ownership. We think in first principles. We follow the data, not the narrative.
              </p>
            </div>
          </section>

          {/* What We Cover */}
          <section>
            <h2 className="text-lg font-bold text-[#F7931A] uppercase tracking-wider mb-6">
              What We Cover
            </h2>
            <div className="grid gap-4">
              {[
                {
                  icon: '₿',
                  title: 'Bitcoin On-Chain Metrics',
                  desc: 'MVRV, SOPR, LTH supply, exchange flows, miner behavior, and realized cap. We track the blockchain directly — not price action, but economic conviction.',
                },
                {
                  icon: '📊',
                  title: 'Federal Reserve & Macro',
                  desc: 'Interest rates, inflation data, yield curve dynamics, dollar strength, and global central bank policy. The macro backdrop shapes every risk asset — including Bitcoin.',
                },
                {
                  icon: '🤖',
                  title: 'AI & Labor Disruption',
                  desc: 'AI adoption curves, job displacement data, enterprise capex, semiconductor supply chains, and regulation. The AI cycle is the macro story of this decade.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-5 rounded-xl border border-white/5 bg-white/[0.02] flex gap-4"
                >
                  <div className="text-2xl leading-none pt-0.5">{item.icon}</div>
                  <div>
                    <div className="text-white font-semibold text-sm mb-1">{item.title}</div>
                    <div className="text-gray-500 text-sm leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Who's behind it */}
          <section>
            <h2 className="text-lg font-bold text-[#F7931A] uppercase tracking-wider mb-4">
              Who's Behind It
            </h2>
            <div className="flex items-start gap-4 mb-5 p-5 rounded-xl border border-white/5 bg-white/[0.02]">
              <div className="w-12 h-12 rounded-full bg-[#F7931A] flex items-center justify-center flex-shrink-0">
                <span className="text-black font-bold text-lg">L</span>
              </div>
              <div>
                <div className="text-white font-semibold text-sm mb-0.5">Lance Roberts</div>
                <div className="text-[#F7931A] text-xs font-medium mb-2">Founder, UTXOMacro</div>
                <div className="text-gray-500 text-xs leading-relaxed">
                  Independent researcher and entrepreneur focused on Bitcoin, macroeconomics, and AI disruption.
                </div>
              </div>
            </div>
            <div className="text-gray-400 leading-relaxed space-y-4 text-sm">
              <p>
                UTXOMacro is an independent operation — one person, one mission. No VC funding. No advertiser agenda. No conflicts of interest.
              </p>
              <p>
                Research is AI-assisted and human-reviewed. Every claim is sourced. Every draft is checked against on-chain data, Fed releases, and verified reporting before it publishes.
              </p>
            </div>
          </section>

          {/* Newsletter CTA */}
          <section className="p-8 rounded-xl border border-[#F7931A]/20 bg-[#F7931A]/5 text-center">
            <div className="text-[#F7931A] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Stay Informed
            </div>
            <h3 className="text-xl font-bold text-white mb-3">
              Get the daily brief in your inbox
            </h3>
            <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
              Bitcoin on-chain signals, macro inflection points, and AI disruption — concise, sourced, and free. Sent twice a week.
            </p>
            <button
              onClick={() => onNavigate('newsletter')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#F7931A] text-black font-bold text-sm hover:bg-[#E8841A] transition-colors"
            >
              Subscribe Free →
            </button>
          </section>
        </div>
      </main>

      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default About;
